/**
 * シフト編成（新）共通スタイル
 * タブレット前提: タップ対象は 44px 以上、hover に依存しない
 */
import { css } from '@emotion/core';
import {
  grayScale, productColor, textColor, textFontSize, utilityColor,
} from 'components/styles';

export const TOUCH_TARGET = 44;

export const boardColors = {
  border: grayScale.gray10,
  borderStrong: grayScale.gray30,
  headerBg: grayScale.gray05,
  weekendSat: '#EAF2FB',
  weekendSun: '#FBEAEA',
  holiday: '#FBEAEA',
  storeHoliday: grayScale.gray10,
  confirmed: '#F2F5F9',
  locked: '#F5F5F5',
  today: productColor.primaryM90,
  away: '#FFF6E5',
  hope: '#EFE6F7',
  warning: '#FDF1DC',
  danger: '#FBEAEA',
  ok: '#E3F2E8',
  selected: productColor.primary,
};

export const boardStyles = {
  page: css({
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 56px)',
    background: '#F5F6F7',
  }),
  toolbar: css({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: utilityColor.white,
    borderBottom: `1px solid ${grayScale.gray10}`,
    position: 'sticky',
    top: 0,
    zIndex: 20,
  }),
  toolbarGroup: css({
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  }),
  iconButton: css({
    minWidth: `${TOUCH_TARGET}px`,
    height: `${TOUCH_TARGET}px`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 10px',
    border: `1px solid ${grayScale.gray10}`,
    borderRadius: '6px',
    background: utilityColor.white,
    color: textColor.main,
    fontSize: textFontSize.re,
    cursor: 'pointer',
    touchAction: 'manipulation',
    '&:disabled': { color: utilityColor.disabled, cursor: 'default' },
    '&[data-active="true"]': { background: productColor.primary, color: utilityColor.white, borderColor: productColor.primary },
  }),
  segment: css({
    display: 'inline-flex',
    border: `1px solid ${grayScale.gray10}`,
    borderRadius: '6px',
    overflow: 'hidden',
    '& > button': {
      minWidth: '56px',
      height: `${TOUCH_TARGET - 4}px`,
      border: 'none',
      background: utilityColor.white,
      color: textColor.main,
      fontSize: textFontSize.re,
      cursor: 'pointer',
    },
    '& > button[data-active="true"]': { background: productColor.primary, color: utilityColor.white },
  }),
  banner: css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    fontSize: textFontSize.sm,
    borderBottom: `1px solid ${grayScale.gray10}`,
  }),
  simulationBar: css({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '6px 12px',
    background: '#FFF6E5',
    borderBottom: '1px solid #F2C94C',
    position: 'sticky',
    top: '61px',
    zIndex: 19,
    fontSize: textFontSize.re,
  }),
  body: css({
    display: 'flex',
    flex: 1,
    minHeight: 0,
  }),
  palette: css({
    position: 'sticky',
    top: '61px',
    alignSelf: 'flex-start',
    width: '132px',
    maxHeight: 'calc(100vh - 70px)',
    overflowY: 'auto',
    padding: '8px',
    background: utilityColor.white,
    borderRight: `1px solid ${grayScale.gray10}`,
    flexShrink: 0,
  }),
  paletteItem: css({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minHeight: `${TOUCH_TARGET}px`,
    padding: '4px 8px',
    marginBottom: '4px',
    borderRadius: '6px',
    border: `1px solid ${grayScale.gray10}`,
    fontSize: textFontSize.sm,
    cursor: 'pointer',
    userSelect: 'none',
    touchAction: 'manipulation',
    '&[data-active="true"]': { outline: `2px solid ${productColor.primary}`, outlineOffset: '-2px' },
  }),
  gridWrap: css({
    flex: 1,
    overflow: 'auto',
    position: 'relative',
    background: utilityColor.white,
  }),
  table: css({
    borderCollapse: 'separate',
    borderSpacing: 0,
    tableLayout: 'fixed',
    fontSize: textFontSize.sm,
    '& th, & td': {
      border: `1px solid ${grayScale.gray10}`,
      padding: 0,
      verticalAlign: 'top',
    },
    '& thead th': {
      position: 'sticky',
      top: 0,
      zIndex: 3,
      background: grayScale.gray05,
    },
  }),
  staffCell: css({
    position: 'sticky',
    left: 0,
    zIndex: 2,
    background: utilityColor.white,
    padding: '4px 8px',
    minHeight: `${TOUCH_TARGET + 8}px`,
    fontSize: textFontSize.sm,
    textAlign: 'left',
  }),
  staffHeadCell: css({
    position: 'sticky',
    left: 0,
    zIndex: 4,
    background: grayScale.gray05,
  }),
  cell: css({
    minHeight: `${TOUCH_TARGET + 8}px`,
    height: '1px',
    padding: '2px',
    cursor: 'pointer',
    touchAction: 'manipulation',
    userSelect: 'none',
    '&[data-locked="true"]': { background: '#F5F5F5', cursor: 'default' },
    '&[data-changed="true"]': { boxShadow: 'inset 0 0 0 2px #F2C94C' },
    '&[data-drop="true"]': { boxShadow: `inset 0 0 0 2px ${productColor.primary}` },
  }),
  cellInner: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minHeight: `${TOUCH_TARGET + 4}px`,
    padding: '2px',
  }),
  footerCell: css({
    padding: '4px',
    textAlign: 'center',
    background: grayScale.gray05,
    fontSize: textFontSize.xs,
    whiteSpace: 'nowrap',
  }),
  totalsCell: css({
    padding: '4px 6px',
    textAlign: 'right',
    fontSize: textFontSize.xs,
    whiteSpace: 'nowrap',
    background: utilityColor.white,
  }),
  subText: css({
    color: textColor.subText01,
    fontSize: textFontSize.xs,
  }),
  modalBody: css({
    padding: '8px 4px',
    minWidth: '320px',
    fontSize: textFontSize.re,
  }),
  formRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  }),
  input: css({
    minHeight: `${TOUCH_TARGET - 4}px`,
    padding: '6px 8px',
    border: `1px solid ${grayScale.gray10}`,
    borderRadius: '4px',
    fontSize: textFontSize.re,
  }),
  listRow: css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minHeight: `${TOUCH_TARGET}px`,
    padding: '4px 0',
    borderBottom: `1px solid ${grayScale.gray10}`,
  }),
  dangerText: css({ color: utilityColor.error }),
  warnText: css({ color: '#B7791F' }),
  okText: css({ color: '#2E7D4F' }),
};

/** モック（hitosuke-shift-mock-demo）の配色。既存トークンに無いものだけここで持つ */
export const mock = {
  primary: '#0D4796',
  primaryP20: '#0A3878',
  primaryM95: '#F2F5F9',
  primaryM90: '#E7EEF5',
  primaryM80: '#CEDAEA',
  secondary: '#D1506E',
  success: '#3BA55D',
  error: '#E95562',
  errorBg: '#FDECEF',
  textMain: '#333333',
  textSub: '#737373',
  gray10: '#EAEAEA',
  gray20: '#D4D4D4',
  gray40: '#A3A3A3',
  gray60: '#737373',
  amberBg: '#FBF3E1',
  amberBorder: '#E0B35A',
  amberText: '#8A5A0E',
  amberMark: '#C98A2B',
  guestBg: '#F8F6FB',
  guestAvatarBg: '#E9E1F3',
  guestText: '#5B3E8C',
  freeBg: '#E4EAF3',
  freeText: '#3B4F73',
  hatch: 'repeating-linear-gradient(45deg, rgba(0,0,0,.16) 0 3px, rgba(0,0,0,0) 3px 7px)',
  shadow: '0 1px 3px 0 rgba(0,0,0,.12)',
};

/** モックのパターン既定色（色未設定のパターンに順番に割り当てる） */
export const MOCK_PATTERN_COLORS: Array<{ bg: string; fg: string }> = [
  { bg: '#DDF2E3', fg: '#2E6B45' },
  { bg: '#FBE9C3', fg: '#7A5A14' },
  { bg: '#EADFF5', fg: '#5B3E8C' },
  { bg: '#DCE9F7', fg: '#1F4E86' },
  { bg: '#FBE0E6', fg: '#8A2F45' },
  { bg: '#E0F1F1', fg: '#1F6B6B' },
  { bg: '#F1EBD9', fg: '#6B5B1F' },
  { bg: '#E4E4E4', fg: '#4A4A4A' },
];

export const mockButton = css({
  background: '#FFFFFF',
  border: `1px solid ${'#D4D4D4'}`,
  borderRadius: '5px',
  color: '#333333',
  fontSize: '12px',
  padding: '8px 14px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flex: 'none',
  fontFamily: 'inherit',
  '&:disabled': { color: '#A3A3A3', cursor: 'default' },
});

export const mockPrimaryButton = css({
  background: '#0D4796',
  border: 'none',
  borderRadius: '5px',
  color: '#FFFFFF',
  fontSize: '13px',
  fontWeight: 700,
  padding: '9px 20px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flex: 'none',
  fontFamily: 'inherit',
  '&:disabled': { background: '#D4D4D4', cursor: 'default' },
});

/** 「人件費」と出す所の説明。人件費は交通費込み（設計書 A1 集計・旧画面の人件費率と同じ式。10/5） */
export const LABOR_COST_TIP = '交通費を含まない（交通費は下の段）';
/** 「交通費」と出す所の説明 */
export const TRANSPORTATION_TIP = '人件費とは別に出す';
/** 人件費率・人件費予算の超過の説明（旧画面と同じく交通費を含めて見る） */
export const LABOR_RATE_TIP = '（人件費 ＋ 交通費）÷ 売上（実績が無ければ予算）';

/**
 * 画面に出す人件費（交通費を除く。10/7）
 *
 * サーバの laborCost は「シフトの人件費 + 交通費」（人件費率・人件費予算の判定と同じ式。旧画面の人件費率と同じ）。
 * 人件費と交通費が同じ列に並ぶと、人件費に交通費が含まれていることが分かりにくく、人件費だけの額も分からないので、
 * 表示では交通費を引いて出す。計算（人件費率・予算超過）は交通費込みのまま。
 */
type Yen = number | null | undefined;
export const laborOnly = (laborCost: Yen, transportation: Yen): number | null => (
  laborCost === null || laborCost === undefined ? null : laborCost - (transportation || 0)
);

/**
 * 公休のカードの見た目（10/7 案 B）
 *
 * 法定／所定を区別する人だけ書き分ける。法定は濃い灰色に白文字、所定は薄い灰色。
 * カードは上に小さく種類（法定／所定）、下に大きく「公休」（シフトのカードの「略称／時刻」と同じ組み方）。
 * 区別しない人の公休は保存すると法定になるが、法定の色は付けず「公休」だけにする（区別する人の法定と紛れないように）。
 * 濃い灰色はパターンの色（淡い色）と紛れず、白黒の印刷でも塗りとして残る。
 */
export type HolidayLook = {
  bg: string;
  fg: string;
  /** 上に小さく出す種類。区別しない人は null */
  kind: '法定' | '所定' | null;
  /** 1 行で出すときの文言（カードが 2 件以上あるとき・コピーのプレビューなど） */
  short: string;
};

export const HOLIDAY_LEGAL_BG = '#666666';

export const holidayLook = (isLegal: boolean | null | undefined, distinguish: boolean): HolidayLook => {
  if (distinguish && isLegal) {
    return {
      bg: HOLIDAY_LEGAL_BG, fg: '#FFFFFF', kind: '法定', short: '公休(法)',
    };
  }
  if (distinguish) {
    return {
      bg: mock.gray10, fg: mock.gray60, kind: '所定', short: '公休(所)',
    };
  }
  return {
    bg: mock.gray10, fg: mock.gray60, kind: null, short: '公休',
  };
};
